import React from "react";
import { projects } from "../../mock";
import { Github, Sparkle } from "lucide-react";

export default function Projects() {
  const featured = projects.filter((p) => p.featured).slice(0, 2);
  const featuredIds = new Set(featured.map((p) => p.id));
  const rest = projects.filter((p) => !featuredIds.has(p.id));

  return (
    <section
      id="projects"
      className="relative py-24 md:py-32 border-t border-zinc-900 bg-[#0a0a0c] overflow-hidden"
    >
      <div className="aurora -bottom-40 -right-40 opacity-20" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 md:px-8">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3 reveal">
            <span className="mono text-xs section-num font-semibold">01 //</span>
            <span className="mono text-xs text-zinc-500 uppercase tracking-[0.2em]">
              Projects
            </span>
          </div>
          <h2 className="display text-4xl md:text-5xl font-semibold reveal">
            Selected <span className="text-zinc-500">Projects.</span>
          </h2>
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5 mb-5">
            {featured.map((p, i) => (
              <ProjectCard key={p.id} project={p} featured index={i} />
            ))}
          </div>
        )}

        {/* Rest */}
        {rest.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            {rest.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProjectCard({ project, featured = false, index = 0 }) {
  return (
    <article
      className={`card-hover group relative border border-zinc-900 rounded-2xl p-6 md:p-7 bg-[#0b0b0d] overflow-hidden reveal ${
        featured ? "md:p-8" : ""
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-[#5eead4]/10 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      {featured && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-[#5eead4] border border-[#5eead4]/30 bg-[#5eead4]/5 px-2 py-1 rounded-full backdrop-blur">
          <Sparkle size={10} /> Featured
        </div>
      )}

      <div className="flex items-center gap-2 mono text-[11px] text-zinc-500">
        <span className="text-[#5eead4]">{project.type}</span>
        <span className="text-zinc-700">/</span>
        <span>{project.year}</span>
      </div>

      <h3
        className={`display font-semibold mt-3 ${
          featured ? "text-2xl md:text-3xl" : "text-xl"
        } text-white`}
      >
        {project.title}
      </h3>
      <p className="text-sm text-[#5eead4]/80 mt-1">{project.tagline}</p>

      <p className="text-sm text-zinc-500 mt-4 leading-relaxed">
        {project.description}
      </p>

      <div className="mt-6 flex items-center justify-between border-t border-zinc-900 pt-4">
        <div className="mono text-xs text-[#5eead4]">{project.metric}</div>
        <a
          href={project.repo}
          target="_blank"
          rel="noreferrer"
          className="h-8 w-8 grid place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:text-[#5eead4] hover:border-[#5eead4]/40 hover:-translate-y-0.5 transition-all"
          aria-label="Repository"
        >
          <Github size={14} />
        </a>
      </div>
    </article>
  );
}
